import React, { MouseEvent } from "react";

import {
  faChevronLeft,
  faChevronRight,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { Callout, VARIANT_STYLES } from "src/design-system/Callout";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";

export type ErrorSection = {
  title: string;
  errorBlob: string;
  isCodeBlock: boolean;
};

type MultipleErrorCalloutProps = {
  onClickPrev: (e: MouseEvent) => void;
  onClickNext: (e: MouseEvent) => void;
  displayedIndex: number;
  totalErrorsCount: number;
  errorSections: ErrorSection[];
  failureCount: number | undefined;
  outdatedTooltipTitle?: string;
  action?: {
    text: string;
    onClick: () => void;
  };
  variant: "error" | "warning";
};

export const MultipleErrorCallout: React.FC<MultipleErrorCalloutProps> = ({
  onClickPrev,
  onClickNext,
  displayedIndex,
  totalErrorsCount,
  errorSections,
  failureCount,
  action,
  outdatedTooltipTitle = "Test run again to update data",
  variant = "error",
}) => {
  const styles = VARIANT_STYLES[variant];
  return (
    <div
      className={twJoin(
        "flex max-h-56 w-full items-stretch gap-x-3 rounded-lg border pr-4 pl-3 font-inter-normal-12px",
        styles.border,
        styles.container,
      )}
      data-loc="error-selector"
      role="alert"
    >
      <Tooltip
        disabled={variant !== "warning"}
        placement="top"
        title={outdatedTooltipTitle}
        asChild
      >
        <div className="flex items-center self-start py-2">
          <button
            type="button"
            aria-label="Previous error"
            onClick={onClickPrev}
          >
            <Icon icon={faChevronLeft} size="xs" variant={variant} />
          </button>

          <Icon icon={faWarning} size="xs" variant={variant} />

          <span className="ml-0.5">
            {`${
              displayedIndex > -1 ? displayedIndex + 1 : "?"
            } of ${totalErrorsCount}${variant === "warning" ? " outdated" : ""} errors`}
          </span>
          <button type="button" aria-label="Next error" onClick={onClickNext}>
            <Icon icon={faChevronRight} size="xs" variant={variant} />
          </button>
        </div>
      </Tooltip>
      <div className={twJoin("border-l", styles.border)} />
      <div className="flex min-w-0 flex-1 items-center justify-between gap-x-3 py-2">
        <div className="error-selector-scrollbar max-h-full min-w-0 flex-1 overflow-auto break-words">
          <Callout.BlobContent items={errorSections} variant={variant} />
        </div>
        {action ? (
          <button
            type="button"
            className="flex-shrink-0 self-start whitespace-nowrap font-inter-medium-12px underline"
            onClick={action.onClick}
          >
            {action.text}
          </button>
        ) : (
          failureCount !== undefined && (
            <span className="flex-shrink-0 self-start">
              {failureCount} failures
            </span>
          )
        )}
      </div>
    </div>
  );
};
